import {
    Box,
    Heading,
    IconButton,
    Toast,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
    useSubPage,
} from "@bcc-ui/ui-components";
import classNames from "classnames";
import * as React from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps, useDispatch, useSelector } from "react-redux";
import { ToastContainer } from "react-toastify";

import DropTenantAccessBanner from "./DropTenantAccessBanner";
import style from "./layout.scss";
import { useAIPanelContext } from "components/ai-panel/AIPanelContext";
import ErrorMessage from "components/error-message/ErrorMessage";
import RefreshButtonIcon from "components/icons/RefreshButtonIcon";
import { MobileNavigationHeader } from "components/navigation/mobile-navigation-header/MobileNavigationHeader";
import { NavigationPanel } from "components/navigation/navigation-panel/NavigationPanel";
import { PROFILE_ROUTE, RouteDefinition, TENANT_PROFILE_ROUTE } from "components/router/Routes";
import TenantTrialEndingBanner from "components/tenants/TenantTrialEndingBanner";
import { AUTH_EMS_CREATE_ENTITLEMENT, AUTH_EMS_SYNCHRONIZE, AUTH_EMS_VIEW } from "domain/authority";
import { Breakpoint } from "domain/breakpoints";
import { licenseService } from "services/licenses/LicenseService";
import { hasSubTenantCookie, hasTenantCookie } from "services/tenants/tenantCookieService";
import { userSessionService } from "services/user/UserSessionService";
import { StoreState } from "store";
import { setInfo } from "store/bmc";
import { setMobileMainNavigationOpen } from "store/mobileNavigation";
import { resolveColor } from "utils/commonFunctions";
import { Logger } from "utils/logging";
import { getObject, RepositoryKey, setObject } from "utils/repository";

import testIds from "testIds.json";

interface Props {
    viewTitle: string | undefined;
    // Use when a description is shown right below the title.
    compactViewTitle?: boolean;
    view: React.JSX.Element | string;
    viewActions?: React.JSX.Element;
    viewActionsClassName?: string;
    hideActions?: React.JSX.Element;
    viewContainer?: boolean;
    mainRoute: RouteDefinition;
    kebabMenu?: React.JSX.Element;
    enableRefresh?: boolean;
    onDialog?: boolean;
    hideDialogView?: (value: boolean) => void;
    onHandleRefresh?: () => void;
    onFetch?: () => void;
    className?: string;
    isAiHomeView?: boolean;
}

export interface SubPage {
    content: JSX.Element | undefined;
    isModal?: boolean;
}
export interface SubpageContext {
    setSubpage: React.Dispatch<React.SetStateAction<SubPage | undefined>>;
    closeSubNavigationPanel: () => void;
}

const LOGGER = new Logger("ApplicationLayout");

let resetMobileSubNavigationOnload = false;

export const viewActionsContext = React.createContext<JSX.Element | undefined>(undefined);
export const subpageContext = React.createContext<SubpageContext | undefined>(undefined);

const mapState = (state: StoreState) => ({
    theme: state.themeReducer.theme,
    tenantDetails: state.tenantDetailsReducer.stack[state.tenantDetailsReducer.stack.length - 1],
});

const connector = connect(mapState, { setInfo });
function ApplicationLayout(rawProps: Props & ConnectedProps<typeof connector>) {
    const props = { viewContainer: true, ...rawProps };
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const ref = React.useRef(null);
    const [subpage, setSubpage] = React.useState<SubPage>({
        content: undefined,
        isModal: false,
    });
    const [hideViewActions, setHideViewActions] = React.useState(false);

    const [isSubNavigationOpened, setIsSubNavigationOpened] = React.useState(() => {
        const isDesktopViewport = window.innerWidth > Breakpoint.MD;

        if (resetMobileSubNavigationOnload) {
            return getObject<boolean>(RepositoryKey.SUB_NAVIGATION) ?? isDesktopViewport;
        }

        resetMobileSubNavigationOnload = true;
        return isDesktopViewport;
    });

    const isMobileMainNavigationOpen = useSelector(
        (state: StoreState) => state.mobileNavigationReducer.isMainNavigationOpen
    );
    const isTenantBannerVisible = hasTenantCookie() || hasSubTenantCookie();
    const [isDismissed, setIsDismissed] = React.useState(getObject(RepositoryKey.BANNER_DISMISSED) ?? false);
    const { setCurrentViewDetails } = useAIPanelContext();
    const { subPage } = useSubPage();

    const isProfileRoute =
        props.mainRoute.path === PROFILE_ROUTE.path || props.mainRoute.path === TENANT_PROFILE_ROUTE.path;

    const handleErrorState = () => {
        setHideViewActions(true);
    };

    const handleSubNavigationPanelChange = (value: boolean) => {
        setObject(RepositoryKey.SUB_NAVIGATION, value);
        setIsSubNavigationOpened(value);
    };

    const closeSubNavigationPanel = () => {
        handleSubNavigationPanelChange(false);
        dispatch(setMobileMainNavigationOpen(false));
    };

    const [hasAttemptedFetch, setHasAttemptedFetch] = React.useState(false);
    const canViewEms = userSessionService.userHasAnyAuthority([
        AUTH_EMS_VIEW,
        AUTH_EMS_CREATE_ENTITLEMENT,
        AUTH_EMS_SYNCHRONIZE,
    ]);

    React.useEffect(() => {
        setCurrentViewDetails(props.viewTitle, t(props.mainRoute.title));
    }, [props.viewTitle, props.mainRoute.title]);

    React.useEffect(() => {
        if (
            !hasAttemptedFetch &&
            props.tenantDetails?.uuid &&
            userSessionService.currentUserDetails() &&
            !hasTenantCookie() &&
            canViewEms
        ) {
            setHasAttemptedFetch(true);

            licenseService
                .fetchEmsConfiguration(props.tenantDetails.uuid)
                .then((emsConfiguration) => {
                    dispatch(
                        setInfo({
                            hlEntitlement: emsConfiguration.hlEntitlements,
                            slEntitlement: emsConfiguration.slEntitlements,
                            licenseExists: false,
                            availableLicenses: 0,
                        })
                    );
                })
                .catch(() => {
                    LOGGER.error("Failed to fetch EMS configuration");
                });
        }
    }, [hasAttemptedFetch, dispatch, props.tenantDetails]);

    React.useEffect(() => {
        const mediaQuery = window.matchMedia(`(max-width: ${Breakpoint.MD}px)`);
        const handleChange = (event: MediaQueryListEvent) => {
            if (event.matches) {
                handleSubNavigationPanelChange(false);
                dispatch(setMobileMainNavigationOpen(false));
            }
        };

        mediaQuery.addEventListener("change", handleChange);

        return () => {
            mediaQuery.removeEventListener("change", handleChange);
        };
    }, []);

    /* TODO BCC-4838 make into a global function */
    const calculateTrialDaysLeft = () => {
        const tenantExpirationDate = props.tenantDetails.expirationDate;
        if (!tenantExpirationDate) {
            return null;
        }

        const trialEndDate = new Date(tenantExpirationDate);
        const today = new Date();

        trialEndDate.setUTCHours(19, 59, 59, 999);
        today.setUTCHours(19, 59, 59, 999);

        const calculateDifference = trialEndDate.getTime() - today.getTime();

        const daysLeft = Math.ceil(calculateDifference / (1000 * 60 * 60 * 24));

        return daysLeft;
    };

    const trialDaysLeft = calculateTrialDaysLeft();

    const handleDismiss = () => {
        setObject(RepositoryKey.BANNER_DISMISSED, true);
        setIsDismissed(true);
    };

    const contextViewActions = () => {
        if (!props.hideActions) {
            return props.viewActions;
        }
        if (props.hideActions && props.viewActions) {
            if (props.viewActions.props.children === undefined) {
                return undefined;
            }

            return React.cloneElement(
                props.viewActions,
                {},
                [props.viewActions.props.children].flat().filter((child) => child !== props.hideActions)
            );
        }
    };

    return (
        <>
            <subpageContext.Provider value={{ setSubpage, closeSubNavigationPanel }}>
                {isTenantBannerVisible && <DropTenantAccessBanner />}

                <MobileNavigationHeader
                    isNavigationOpen={isMobileMainNavigationOpen}
                    onToggle={() => {
                        if (!isMobileMainNavigationOpen) {
                            dispatch(setMobileMainNavigationOpen(true));
                            if (!isProfileRoute) {
                                handleSubNavigationPanelChange(true);
                            }
                        } else {
                            dispatch(setMobileMainNavigationOpen(false));
                            handleSubNavigationPanelChange(false);
                        }
                    }}
                    isTenantBannerVisible={isTenantBannerVisible}
                />

                <div
                    className={classNames(style.content, style.gridLayout, {
                        ["subNavigationVisible"]: isSubNavigationOpened,
                        [style.mobileNavigationOpen]: isMobileMainNavigationOpen,
                        ["tenantBannerVisible"]: isTenantBannerVisible,
                    })}
                >
                    <NavigationPanel
                        isTrialTenant={props.tenantDetails.contractType === "TRIAL"}
                        isSubNavigationOpened={isSubNavigationOpened}
                        onSubNavigationOpenChange={handleSubNavigationPanelChange}
                        isMobileMainNavigationOpen={isMobileMainNavigationOpen}
                        isTenantBannerVisible={isTenantBannerVisible}
                    />

                    {(isMobileMainNavigationOpen || isSubNavigationOpened) && (
                        <div
                            className={style.backdrop}
                            onClick={() => {
                                if (isSubNavigationOpened) {
                                    handleSubNavigationPanelChange(false);
                                }
                                if (isMobileMainNavigationOpen) {
                                    dispatch(setMobileMainNavigationOpen(false));
                                }
                            }}
                            data-testid={testIds.common.mobileNavigationBackdrop}
                        />
                    )}

                    <div
                        className={classNames(style.view, { [style.subpage]: subpage }, props.className)}
                        tabIndex={-1}
                        ref={ref}
                    >
                        {subPage ? (
                            subPage
                        ) : subpage.content && !subpage.isModal ? (
                            subpage.content
                        ) : (
                            <div className={style.mainView}>
                                {trialDaysLeft !== null &&
                                    trialDaysLeft <= 7 &&
                                    props.tenantDetails.contractType !== "PRODUCTION" && (
                                        <TenantTrialEndingBanner
                                            visible={!isDismissed}
                                            days={trialDaysLeft}
                                            onDismiss={handleDismiss}
                                        />
                                    )}

                                {subpage.content && subpage.isModal && subpage.content}

                                <Box
                                    justifyContent="SpaceBetween"
                                    gap={"Md"}
                                    paddingBottom={props.compactViewTitle ? "Xxs" : "Sm"}
                                    flexWrap={"Wrap"}
                                    className={classNames({
                                        [style.isHidden]: subpage.content !== undefined,
                                        [style.hiddenOnMobileAiHome]: props.isAiHomeView,
                                    })}
                                >
                                    <Box
                                        horizontalAlignment={"Left"}
                                        verticalAlignment={"Center"}
                                        gap={"Xxs"}
                                        onClick={() => {
                                            if (typeof props.onFetch !== "undefined") {
                                                props.onFetch();
                                            }
                                        }}
                                        flexWrap={"Wrap"}
                                    >
                                        {!hideViewActions && props.kebabMenu}

                                        {props.viewTitle && (
                                            <Heading
                                                variant={"h1"}
                                                disableBottomSpacing
                                                testId={testIds.common.primaryView.titleLabel}
                                            >
                                                {props.viewTitle}
                                            </Heading>
                                        )}

                                        {props.viewTitle && props.enableRefresh && !hideViewActions && (
                                            <Tooltip>
                                                <TooltipContent>{t("Notification.title.refreshTable")}</TooltipContent>
                                                <TooltipTrigger>
                                                    <IconButton
                                                        testId={testIds.common.primaryView.refreshButton}
                                                        onClick={() => {
                                                            if (typeof props.onHandleRefresh !== "undefined") {
                                                                props.onHandleRefresh();
                                                            }
                                                        }}
                                                    >
                                                        <RefreshButtonIcon
                                                            color={resolveColor("--theme-icon-fill-default")}
                                                        />
                                                    </IconButton>
                                                </TooltipTrigger>
                                            </Tooltip>
                                        )}
                                    </Box>

                                    {props.viewActions && !hideViewActions && (
                                        <Box gap={"Sm"} flexWrap={"Wrap"} className={props.viewActionsClassName}>
                                            {props.viewActions}
                                        </Box>
                                    )}
                                </Box>

                                <ErrorBoundary FallbackComponent={ErrorMessage} onError={handleErrorState}>
                                    <viewActionsContext.Provider value={contextViewActions()}>
                                        <div
                                            className={
                                                subpage.content !== undefined
                                                    ? style.isHidden
                                                    : props.viewContainer
                                                      ? style.viewContent
                                                      : ""
                                            }
                                        >
                                            {props.view}
                                        </div>
                                    </viewActionsContext.Provider>
                                </ErrorBoundary>
                            </div>
                        )}
                    </div>
                </div>
            </subpageContext.Provider>
            <ToastContainer
                position="top-right"
                autoClose={7000}
                hideProgressBar
                newestOnTop={false}
                closeOnClick
                className={style.customToastContainer}
                bodyClassName={style.customToastBody}
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
            <Toast />
        </>
    );
}

export default connector(ApplicationLayout);
